import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import {
    AltitudeValueDto,
    DisplayableMissionDto,
    MissionOverlapAcknowledgementDto,
    MissionOverlapDto,
    SimpleMissionDto,
    UserService
} from '@flyfreely-portal-ui/flyfreely';
import { FeatureGroup } from '@flyfreely-portal-ui/map';
import { MODAL_OPTIONS } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import moment, { Moment } from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MissionOverlapDetailsComponent } from '../mission-overlap-details/mission-overlap-details.component';

@Component({
    selector: 'mission-overlap-summary',
    templateUrl: './mission-overlap-summary.component.html'
})
export class MissionOverlapSummaryComponent
    implements OnInit, OnDestroy, OnChanges
{
    @Input() overlappingMission: SimpleMissionDto;
    @Input() acknowledgementList: Array<MissionOverlapAcknowledgementDto>;
    /**
     * The host mission
     */
    @Input() mission: DisplayableMissionDto;
    @Input() organisationId: number;
    @Input() locationFeatures: FeatureGroup[] = [];
    @Input() overlapStatus: MissionOverlapDto.Status =
        MissionOverlapDto.Status.APPLICABLE;

    private ngUnsubscribe$ = new Subject<void>();

    isAcknowledged = false;

    endTime: Moment | null;
    altitude: AltitudeValueDto | null;

    constructor(
        private userService: UserService,
        private modalService: BsModalService
    ) {}

    ngOnInit(): void {
        this.refreshAcknowledged();
        this.refreshDerivedValues();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('acknowledgementList' in changes) {
            this.refreshAcknowledged();
        }
        if ('overlappingMission' in changes) {
            this.refreshDerivedValues();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshDerivedValues() {
        if (this.overlappingMission == null) {
            this.endTime = null;
            this.altitude = null;
            return;
        }
        this.endTime = moment(this.overlappingMission.missionDate).add(
            this.overlappingMission.missionEstimatedTime,
            'seconds'
        );

        this.altitude =
            this.overlappingMission.maximumHeight != null
                ? {
                      datum: 'AGL',
                      height: this.overlappingMission.maximumHeight,
                      units: 'FT'
                  }
                : null;
    }

    get isInformational(): boolean {
        return this.overlapStatus === MissionOverlapDto.Status.INFORMATIONAL;
    }

    get buttonText(): string {
        return this.isInformational || this.isAcknowledged
            ? 'View'
            : 'Acknowledge';
    }

    refreshAcknowledged() {
        const currentUserId = this.userService.getCurrentUser().id;
        this.isAcknowledged = this.acknowledgementList.some(
            item => item.personId === currentUserId
        );
    }

    viewOverlapDetail() {
        const dialogue = this.modalService.show<MissionOverlapDetailsComponent>(
            MissionOverlapDetailsComponent,
            {
                ...MODAL_OPTIONS,
                class: 'modal-lg',
                initialState: {
                    overlappingMission: this.overlappingMission,
                    missionId: this.mission.id,
                    isAcknowledged: this.isAcknowledged,
                    organisationId: this.organisationId,
                    locationFeatures: this.locationFeatures,
                    overlapStatus: this.overlapStatus
                }
            }
        );
        dialogue.content.acknowledge
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(dialogue.onHide))
            .subscribe(acknowledgement => {
                this.acknowledgementList.push(acknowledgement);
                this.refreshAcknowledged();
            });
    }
}
